import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import {
	AdminConnexZoneOption,
	AdminXpressConnexService,
	AppDataService,
	ConfigService,
	DeliveryOrderService
} from 'app/services';
import {
	BookingDetailTab,
	CancelDeliveryOrderRequest,
	CompleteDeliveryOrderRequest,
	GetNearbyDriversForAssignItem,
	GetNearbyDriversForAssignRequest,
	PrioritizeOrderRequest,
	XpressDeliveryOrder,
	XpressDeliveryOrderListDto
} from '@shared/models';
import { ConfirmationDialogService } from '@shared/services/confirmation-dialog.service';
import {
	ActiveOrdersModel,
	BookingSourceEnum,
	BookingSourceModel,
	CancelReasonTypeModel,
	ExportFileTypeEnum,
	FieldTypeEnum,
	GetConnexBookingsStatsResponse,
	ReasonModel,
	ReasonTypeEnum,
	RiderVehicleType,
	RiderVehicleTypeEnum,
	MenuPermissionActionEnum,
	ServiceTypeEnum,
	XpressDeliveryStatus,
	XpressUserRoleEnum
} from 'app/models';
import { StylesStatus } from 'app/models/users/StylesStatus';
import { Utils } from 'app/utils';
import { OrderUtils } from 'app/utils/order-utils';
import { ColumnSelectorComponent } from 'app/column-selector/column-selector.component';
import { EditTableConfig } from 'app/models/configs/edit-table-config';
import { MessageService } from 'primeng/api';
import { TableLazyLoadEvent } from 'primeng/table';
import moment from 'moment';
import { ConnexWalkInBookingDrawerComponent } from './connex-walk-in-booking-drawer/connex-walk-in-booking-drawer.component';

interface ConnexBookingStatusFilter {
	name: string;
	value: XpressDeliveryStatus[] | null;
	roleId: XpressUserRoleEnum | null;
}

interface ConnexHeaderSearch {
	bookingId: number | null;
	bookingStatusObj: ConnexBookingStatusFilter | null;
	customerName: string | null;
	pickupLocation: AdminConnexZoneOption | null;
	dropOffLocation: AdminConnexZoneOption | null;
	scheduledAt: Date | null;
	createdAt: Date | null;
	pax: number | null;
	reasonType: ReasonTypeEnum | null;
	vehicleType: RiderVehicleTypeEnum | null;
	bookingSource: BookingSourceEnum | null;
	riderName: string | null;
	netFare: number | null;
	paymentMethod: number | null;
}

interface ConnexTableColumn {
	field: string;
	header: string;
	disableSort?: boolean;
	active?: boolean;
	fieldType: FieldTypeEnum;
	model: keyof ConnexHeaderSearch;
	placeholder: string;
	options?: unknown[];
	take?: 'obj';
	optionLabel?: string;
	optionValue?: string;
}

interface ConnexKpiCard {
	title: string;
	hint: string;
	icon: string;
	value: number;
}

@Component({
	selector: 'connex-bookings',
	templateUrl: './connex-bookings.component.html',
	styleUrls: ['./connex-bookings.component.scss']
})
export class ConnexBookingsComponent implements OnInit, OnDestroy {
	private static readonly pesoFmt = new Intl.NumberFormat('en-PH', {
		maximumFractionDigits: 0,
		minimumFractionDigits: 0
	});
	@Input()
	public menuPermissionActive!: string;

	@ViewChild(ColumnSelectorComponent)
	public columnSelector!: ColumnSelectorComponent;

	@ViewChild('walkInDrawer')
	public walkInDrawer!: ConnexWalkInBookingDrawerComponent;

	public readonly rows = 10;
	public readonly tableName = EditTableConfig.CONNEX_BOOKINGS;
	/** flex — тело таблицы занимает только оставшееся место под KPI и футер p-table */
	public readonly tableBodyScrollHeight = 'flex';
	public readonly fieldTypeEnum = FieldTypeEnum;

	public bookingStatusList = ActiveOrdersModel.BookingStatusList;
	public paymentMethodOptions = ActiveOrdersModel.BookingPaymentMethodList;
	public bookingSourceOptions = BookingSourceModel.List;
	public reasonList = ReasonModel.ReasonList;
	public riderVehicleTypeList = this.appData.isDumagueteLocation
		? RiderVehicleType.RiderVehicleTypeDumagueteList
		: this.appData.isBoracayLocation
			? RiderVehicleType.RiderVehicleTypeBoracayList
			: RiderVehicleType.RiderVehicleTypeXpressList;

	public connexZoneOptions: AdminConnexZoneOption[] = [];

	public headerSearch: ConnexHeaderSearch = {
		bookingId: null,
		bookingStatusObj: null,
		customerName: null,
		pickupLocation: null,
		dropOffLocation: null,
		scheduledAt: null,
		createdAt: null,
		pax: null,
		reasonType: null,
		vehicleType: null,
		bookingSource: null,
		riderName: null,
		netFare: null,
		paymentMethod: null
	};

	public defaultCols: ConnexTableColumn[] = [];
	public cols: ConnexTableColumn[] = [];

	public first = 0;
	public loading = true;
	public totalRecords = 0;
	public bookings: XpressDeliveryOrder[] = [];
	public stats: GetConnexBookingsStatsResponse = {
		Success: true,
		TotalToday: 0,
		AwaitingDriver: 0,
		Live: 0,
		Completed: 0,
		Issues: 0,
		ShowConnexService: false
	};

	public sidebarOpen = false;
	public selectedOrder: XpressDeliveryOrder | null = null;

	public forceAssignDialogVisible = false;
	public forceAssignLoading = false;
	public nearbyDrivers: GetNearbyDriversForAssignItem[] = [];
	public forceAssignIsConnex = true;
	public forceAssignOrderId?: number;
	public bookingDetailVisibleTabs: BookingDetailTab[] = [
		BookingDetailTab.Details,
		BookingDetailTab.People,
		BookingDetailTab.Payment,
		BookingDetailTab.Messages,
		BookingDetailTab.Timeline
	];

	private lastSortField = 'Id';
	private lastSortOrder = -1;
	private filterDebounceTimer: number | undefined;
	private pendingInitialTableLazyLoadSkip = true;

	constructor(
		public configService: ConfigService,
		private appData: AppDataService,
		private deliveryOrderService: DeliveryOrderService,
		private adminConnexService: AdminXpressConnexService,
		private messageService: MessageService,
		private confirmationDialogService: ConfirmationDialogService
	) {}

	get tableMinWidth(): string {
		return `${Math.max(this.cols.length, 1) * 16.25}rem`;
	}

	get kpiCards(): ConnexKpiCard[] {
		return [
			{
				title: 'Total Today',
				hint: 'All ConneX Bookings',
				icon: 'pi pi-list',
				value: this.stats.TotalToday ?? 0
			},
			{
				title: 'Awaiting Driver',
				hint: 'Open - needs assignment',
				icon: 'pi pi-hourglass',
				value: this.stats.AwaitingDriver ?? 0
			},
			{
				title: 'Live',
				hint: 'Accepted - On trip',
				icon: 'pi pi-car',
				value: this.stats.Live ?? 0
			},
			{
				title: 'Completed',
				hint: 'Successful trips',
				icon: 'pi pi-check-circle',
				value: this.stats.Completed ?? 0
			},
			{
				title: 'Issues',
				hint: 'Cancellations + no-shows',
				icon: 'pi pi-exclamation-triangle',
				value: this.stats.Issues ?? 0
			}
		];
	}

	get canCreateWalkInBooking(): boolean {
		return !!this.menuPermissionActive
			&& this.appData.hasPermission(this.menuPermissionActive, MenuPermissionActionEnum.Create);
	}

	ngOnInit(): void {
		this.defaultCols = this.buildColumns();
		this.cols = this.defaultCols.map(col => ({ ...col }));
		void this.loadConnexZones();
		void this.loadStats();
		void this.loadBookings({
			first: 0,
			rows: this.rows,
			sortField: this.lastSortField,
			sortOrder: this.lastSortOrder
		});
	}

	ngOnDestroy(): void {
		if (this.filterDebounceTimer) {
			window.clearTimeout(this.filterDebounceTimer);
		}
	}

	onBookingsTableLazyLoad(event: TableLazyLoadEvent): void {
		if (this.columnSelector) {
			this.columnSelector.loadTableSettings(this.tableName);
		}

		if (this.pendingInitialTableLazyLoadSkip) {
			this.pendingInitialTableLazyLoadSkip = false;
			return;
		}
		void this.loadBookings(event);
	}

	onSelectedColumnsChange(event: ConnexTableColumn[]): void {
		if (event?.length > 0) {
			this.cols = event;
			return;
		}
		this.cols = this.defaultCols.map(col => ({ ...col }));
	}

	handleHeaderFilterChange(): void {
		if (this.filterDebounceTimer) {
			window.clearTimeout(this.filterDebounceTimer);
		}
		this.filterDebounceTimer = window.setTimeout(() => {
			this.filterDebounceTimer = undefined;
			this.first = 0;
			void this.loadBookings({
				first: 0,
				rows: this.rows,
				sortField: this.lastSortField,
				sortOrder: this.lastSortOrder
			});
		}, 400);
	}

	handleFilterChange(_field: string, _event: unknown): void {
		this.handleHeaderFilterChange();
	}

	onChangeHeaderFilterDropDown(model: string, event: unknown): void {
		if (model === 'StatusText' && event === -1) {
			this.headerSearch.bookingStatusObj = null;
		}
		this.handleHeaderFilterChange();
	}

	onRowSelect(order: XpressDeliveryOrder): void {
		this.selectedOrder = order;
		this.sidebarOpen = true;
	}

	onSidebarClose(): void {
		this.sidebarOpen = false;
		this.selectedOrder = null;
	}

	onCancelTrip(orderId: number): void {
		this.confirmationDialogService
			.confirmCancelTrip(
				'Cancel this Ride?',
				`You're about to cancel the current booking. This will notify both the driver and the customer.
				Please select a reason for cancellation to proceed.`,
				'Cancel',
				'Confirm Cancellation',
				CancelReasonTypeModel.CancelConnexReasonTypeList
			)
			.subscribe((response: { success?: boolean; reasonType?: number; reasonText?: string }) => {
				if (!response?.success) {
					return;
				}

				this.loading = true;
				this.onSidebarClose();

				const dto: CancelDeliveryOrderRequest = {
					Id: orderId,
					CancelReasonType: response.reasonType,
					CancelReason: response.reasonText ?? 'Order canceled by Administrator'
				};

				this.deliveryOrderService.cancelDeliveryOrder(dto, this.messageService)
					.then(data => {
						if (!data) {
							this.loading = false;
							return;
						}

						if (!data.Success) {
							this.messageService.add({
								severity: 'error',
								summary: 'Error',
								detail: data.ErrorMessage
							});
							this.loading = false;
							return;
						}

						this.messageService.add({
							severity: 'success',
							summary: 'Booking Cancelled Successfully',
							detail: `Booking #${orderId} has been cancelled successfully`
						});

						this.refreshBookings();
						this.loading = false;
					})
					.catch(err => {
						this.loading = false;
						throw err;
					});
			});
	}

	onCompleteTrip(orderId: number): void {
		this.confirmationDialogService.info(
			'Complete Trip?',
			'Mark this trip as completed? This will override the current status and update both rider and driver records.',
			'Mark as Completed',
			'Cancel'
		).subscribe((confirmed: boolean) => {
			if (!confirmed) {
				return;
			}

			this.loading = true;
			this.onSidebarClose();

			const dto: CompleteDeliveryOrderRequest = {
				OrderId: orderId
			};

			this.deliveryOrderService.completeOrder(dto, this.messageService)
				.then(data => {
					if (!data) {
						this.loading = false;
						return;
					}

					if (!data.Success) {
						this.messageService.add({
							severity: 'error',
							summary: 'Error',
							detail: data.ErrorMessage
						});
						this.loading = false;
						return;
					}

					this.messageService.add({
						severity: 'success',
						summary: 'Trip Completed',
						detail: `Trip #${orderId} has been marked as completed`
					});

					this.refreshBookings();
					this.loading = false;
				})
				.catch(err => {
					this.loading = false;
					throw err;
				});
		});
	}

	onForceAssign(orderId: number): void {
		this.forceAssignOrderId = orderId;
		this.forceAssignDialogVisible = true;
		this.forceAssignLoading = true;
		this.nearbyDrivers = [];
		this.forceAssignIsConnex = true;

		const dto: GetNearbyDriversForAssignRequest = {
			OrderId: orderId
		};

		this.deliveryOrderService.getNearbyDriversForAssign(dto, this.messageService)
			.then(data => {
				if (!data) {
					this.forceAssignLoading = false;
					return;
				}

				if (!data.Success) {
					this.messageService.add({
						severity: 'error',
						summary: 'Error',
						detail: data.ErrorMessage
					});
					this.forceAssignLoading = false;
					this.forceAssignDialogVisible = false;
					return;
				}

				this.nearbyDrivers = data.Riders || [];
				this.forceAssignIsConnex = data.IsConnex ?? true;
				this.forceAssignLoading = false;
			})
			.catch(err => {
				this.forceAssignLoading = false;
				this.forceAssignDialogVisible = false;
				throw err;
			});
	}

	onDriverAssigned(): void {
		this.forceAssignDialogVisible = false;
		this.onSidebarClose();
		this.refreshBookings();
	}

	openWalkInBooking(): void {
		if (!this.canCreateWalkInBooking || !this.stats.ShowConnexService) {
			return;
		}
		this.walkInDrawer.open();
	}

	onWalkInBookingCreated(): void {
		this.refreshBookings();
	}

	onPrioritizeRequest(orderId: number): void {
		this.confirmationDialogService.info(
			'Prioritize Request?',
			'Do you want to prioritize this request? It will be moved to the top of the booking queue.',
			'Yes, Prioritize',
			'Cancel'
		).subscribe((confirmed: boolean) => {
			if (!confirmed) {
				return;
			}

			this.loading = true;
			this.onSidebarClose();

			const dto: PrioritizeOrderRequest = {
				OrderId: orderId
			};

			this.deliveryOrderService.prioritizeOrder(dto, this.messageService)
				.then(data => {
					if (!data) {
						this.loading = false;
						return;
					}

					if (!data.Success) {
						this.messageService.add({
							severity: 'error',
							summary: 'Error',
							detail: data.ErrorMessage
						});
						this.loading = false;
						return;
					}

					this.messageService.add({
						severity: 'success',
						summary: 'Request Prioritized',
						detail: `Trip #${orderId} has been prioritized successfully`
					});

					this.refreshBookings();
					this.loading = false;
				})
				.catch(err => {
					this.loading = false;
					throw err;
				});
		});
	}

	private refreshBookings(): void {
		void this.loadStats();
		void this.loadBookings({
			first: this.first,
			rows: this.rows,
			sortField: this.lastSortField,
			sortOrder: this.lastSortOrder
		});
	}

	formatBookingId(id: number | undefined | null): string {
		if (id == null) {
			return '—';
		}
		return String(id);
	}

	getStatusStyles(row: XpressDeliveryOrder): StylesStatus {
		const localStatus = OrderUtils.getActiveOrderLocalStatusText(row.StatusId);
		return OrderUtils.getActiveOrderStatusStyles(localStatus);
	}

	fareLabel(amount: number | undefined | null): string {
		if (amount == null) {
			return '—';
		}
		return `₱ ${ConnexBookingsComponent.pesoFmt.format(amount)}`;
	}

	paxLabel(count: number | undefined | null): string {
		if (count == null) {
			return '—';
		}
		return String(count);
	}

	getDropdownOptions(col: ConnexTableColumn): unknown[] {
		if (col.field === 'PickupLocation' || col.field === 'DropOffLocation') {
			return this.connexZoneOptions;
		}
		if (col.field === 'SourceText') {
			return this.bookingSourceOptions;
		}
		return col.options ?? [];
	}

	private async loadConnexZones(): Promise<void> {
		const response = await this.adminConnexService.getConnexZonesForBookings(this.messageService);
		if (!response?.Success) {
			if (response?.ErrorMessage) {
				this.messageService.add({ severity: 'error', summary: 'Error', detail: response.ErrorMessage });
			}
			return;
		}
		// Не заменяем массив — колонки держат ссылку на connexZoneOptions из buildColumns()
		this.connexZoneOptions.length = 0;
		const items = response.Items ?? [];
		if (items.length > 0) {
			this.connexZoneOptions.push(...items);
		}
	}

	private async loadStats(): Promise<void> {
		const response = await this.deliveryOrderService.getConnexBookingsStats(
			{
				ClientLocationType: this.appData.getClientLocationType(),
				TimeZoneOffset: Utils.getTimezoneOffset()
			},
			this.messageService
		);
		if (!response?.Success) {
			if (response?.ErrorMessage) {
				this.messageService.add({ severity: 'error', summary: 'Error', detail: response.ErrorMessage });
			}
			return;
		}
		this.stats = response;
	}

	private async loadBookings(event: TableLazyLoadEvent | { first?: number; rows?: number; sortField?: string; sortOrder?: number }): Promise<void> {
		const first = event.first ?? 0;
		const rows = event.rows ?? this.rows;

		// PrimeNG lazy load при пагинации шлёт sortOrder: 1 без sortField — не считаем это сменой сортировки
		let sortField = this.lastSortField;
		let sortOrder = this.lastSortOrder;
		if (event.sortField != null && event.sortField !== '') {
			const sf = event.sortField;
			sortField = Array.isArray(sf) ? String(sf[0]) : String(sf);
			sortOrder = event.sortOrder === -1 ? -1 : 1;
		}

		this.lastSortField = sortField;
		this.lastSortOrder = sortOrder;
		this.first = first;
		this.loading = true;

		const bookingStatusObj = this.headerSearch.bookingStatusObj;

		const dto: Record<string, unknown> = {
			Skip: first,
			Take: rows,
			SortField: sortField,
			SortOrder: sortOrder,
			ServiceTypes: [ServiceTypeEnum.Connex],
			ClientLocationType: this.appData.getClientLocationType(),
			TimeZoneOffSet: Utils.getTimezoneOffset(),
			BookingId: this.headerSearch.bookingId ?? null,
			Statuses: bookingStatusObj?.value ?? null,
			CancelRole: bookingStatusObj?.roleId ?? null,
			VehicleType: this.headerSearch.vehicleType ?? null,
			PaymentMethod: this.headerSearch.paymentMethod ?? null,
			CreatedAt: this.headerSearch.createdAt
				? moment(Utils.utcToLocalDate(this.headerSearch.createdAt)).format('YYYY-MM-DDTHH:mm:ss')
				: null,
			ScheduledAt: this.headerSearch.scheduledAt
				? moment(Utils.utcToLocalDate(this.headerSearch.scheduledAt)).format('YYYY-MM-DDTHH:mm:ss')
				: null,
			CustomerName: String(this.headerSearch.customerName ?? '').trim(),
			PickupLocation: this.headerSearch.pickupLocation?.Address?.trim() ?? '',
			DropOffLocation: this.headerSearch.dropOffLocation?.Address?.trim() ?? '',
			RiderName: String(this.headerSearch.riderName ?? '').trim(),
			ReasonType: this.headerSearch.reasonType ?? null,
			PaxCount: this.headerSearch.pax ?? null,
			BookingSource: this.headerSearch.bookingSource ?? null,
			NetFare: this.headerSearch.netFare ?? null,
			FileType: ExportFileTypeEnum.CSV
		};

		try {
			const response = (await this.deliveryOrderService.getConnexDeliveryOrders(
				dto,
				this.messageService
			)) as XpressDeliveryOrderListDto;

			if (!response?.Success) {
				this.messageService.add({
					severity: 'error',
					summary: 'Error',
					detail: response?.ErrorMessage ?? 'Unexpected error occurred'
				});
				return;
			}

			this.bookings = response.Items ?? [];
			this.totalRecords = response.TotalRecords ?? 0;
		} finally {
			this.loading = false;
		}
	}

	private buildColumns(): ConnexTableColumn[] {
		return [
			{
				field: 'Id',
				header: 'Booking ID',
				active: true,
				fieldType: FieldTypeEnum.InputNumber,
				model: 'bookingId',
				placeholder: 'Type here'
			},
			{
				field: 'StatusText',
				header: 'Status',
				active: true,
				fieldType: FieldTypeEnum.Dropdown,
				model: 'bookingStatusObj',
				options: this.bookingStatusList,
				take: 'obj',
				placeholder: 'Select'
			},
			{
				field: 'CustomerName',
				header: 'Customer Name',
				active: true,
				fieldType: FieldTypeEnum.Inputs,
				model: 'customerName',
				placeholder: 'Type here'
			},
			{
				field: 'PickupLocation',
				header: 'Pickup Location',
				active: true,
				fieldType: FieldTypeEnum.Dropdown,
				model: 'pickupLocation',
				options: this.connexZoneOptions,
				take: 'obj',
				optionLabel: 'Address',
				placeholder: 'Select'
			},
			{
				field: 'DropOffLocation',
				header: 'Drop Off Location',
				active: true,
				fieldType: FieldTypeEnum.Dropdown,
				model: 'dropOffLocation',
				options: this.connexZoneOptions,
				take: 'obj',
				optionLabel: 'Address',
				placeholder: 'Select'
			},
			{
				field: 'ScheduledAt',
				header: 'Scheduled Date & Time',
				active: true,
				fieldType: FieldTypeEnum.Calendar,
				model: 'scheduledAt',
				placeholder: 'Type here'
			},
			{
				field: 'CreatedAt',
				header: 'Date and Time',
				active: true,
				fieldType: FieldTypeEnum.Calendar,
				model: 'createdAt',
				placeholder: 'Type here'
			},
			{
				field: 'PaxCount',
				header: 'Pax',
				disableSort: true,
				active: true,
				fieldType: FieldTypeEnum.InputNumber,
				model: 'pax',
				placeholder: 'Type here'
			},
			{
				field: 'Reason',
				header: 'Reason',
				disableSort: true,
				active: true,
				fieldType: FieldTypeEnum.Dropdown,
				model: 'reasonType',
				options: this.reasonList,
				placeholder: 'Select'
			},
			{
				field: 'VehicleTypeId',
				header: 'Vehicle Type',
				active: true,
				fieldType: FieldTypeEnum.Dropdown,
				model: 'vehicleType',
				options: this.riderVehicleTypeList,
				placeholder: 'Select'
			},
			{
				field: 'SourceText',
				header: 'Source',
				disableSort: true,
				active: true,
				fieldType: FieldTypeEnum.Dropdown,
				model: 'bookingSource',
				options: this.bookingSourceOptions,
				placeholder: 'Select'
			},
			{
				field: 'RiderName',
				header: 'Driver Name',
				active: true,
				fieldType: FieldTypeEnum.Inputs,
				model: 'riderName',
				placeholder: 'Type here'
			},
			{
				field: 'Amount',
				header: 'Fare amount',
				active: true,
				fieldType: FieldTypeEnum.InputNumber,
				model: 'netFare',
				placeholder: 'Type here'
			},
			{
				field: 'PaymentMethod',
				header: 'Payment method',
				disableSort: true,
				active: true,
				fieldType: FieldTypeEnum.Dropdown,
				model: 'paymentMethod',
				options: this.paymentMethodOptions,
				placeholder: 'Select'
			}
		];
	}
}
